import { fetchReceiptSettings, updateReceiptSettings, resetBranchTokenNumber } from '../../services/branchApi.js';
import { fetchProducts, fetchProductCategories } from '../../services/inventoryApi.js';
import { initializeOfflineDb, deleteDb, getOfflineProducts, getOfflineCategories, getOfflineBranchSettings, syncOfflineOrdersToServer, readOfflineFlag, setOfflineBranchSetting } from '../../services/offlineDb.js';

export const branchSetupStore = (set, get) => ({
    isFirstFetch: true,
    offlineMode: false,
    offlineSyncing: false,
    offlineSyncError: null,
    branchReceiptData: {
        shopName: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        taxId: '',
        currency: 'PKR',
        footerMessage: '',
        showTaxOnReceipt: true,
        taxPercentage: 8.5,
        returnPolicy: '',
        enableAutoDiscount: false,
        discountType: 'fixed',
        discountValue: 0,
        shiftStartHour: 15,
        tokenNumber: null,
        tokenPapers: 1,
        sendTokenToStaff: true,
        offlineMode: false
    },

    // create a fetch function to get the branch receipt data from the backend and set it in the store
    fetchBranchReceiptData: async (branchId) => {
        const normalizedBranchId = String(branchId || '').replace(/^BR-/, '');
        if (!normalizedBranchId) {
            return;
        }

        try {
            const data = await fetchReceiptSettings(normalizedBranchId);
            const receiptDataRows = data.receiptSettings || [];
            const row = receiptDataRows[0] || {};
            const receiptData = {
                shopName: row.shop_name || '',
                address: row.address || '',
                phone: row.phone || '',
                email: row.email || '',
                website: row.website || '',
                taxId: row.tax_id || '',
                currency: row.currency || 'PKR',
                footerMessage: row.footer_message || '',
                showTaxOnReceipt: Boolean(row.show_tax_on_receipt),
                taxPercentage: Number(row.tax_percentage || 0),
                returnPolicy: row.return_policy || '',
                enableAutoDiscount: Boolean(row.enable_auto_discount),
                discountType: row.discount_type || 'fixed',
                discountValue: Number(row.discount_value || 0),
                shiftStartHour: (data.branchSettings && typeof data.branchSettings.shift_start_hour !== 'undefined') ? Number(data.branchSettings.shift_start_hour) : 15,
                tokenNumber: (data.branchSettings && typeof data.branchSettings.token_number !== 'undefined') ? Number(data.branchSettings.token_number) : null,
                tokenPapers: (data.branchSettings && typeof data.branchSettings.token_papers !== 'undefined') ? Number(data.branchSettings.token_papers) : 1,
                sendTokenToStaff: (data.branchSettings && typeof data.branchSettings.send_token_to_staff !== 'undefined') ? Boolean(data.branchSettings.send_token_to_staff) : true,
                offlineMode: (data.branchSettings && typeof data.branchSettings.offline_mode !== 'undefined') ? Boolean(data.branchSettings.offline_mode) : false
            };
            set({ branchReceiptData: receiptData, offlineMode: receiptData.offlineMode, isFirstFetch: false });
            return receiptData;
        } catch (error) {
            console.error('Error fetching branch receipt data:', error);
            try {
                const offlineData = await getOfflineBranchSettings();
                if (offlineData && offlineData.offline_mode === '1') {
                    set({ branchReceiptData: {
                        ...get().branchReceiptData,
                        tokenNumber: Number(offlineData.token_number || 1),                        tokenPapers: Number(offlineData.token_papers || get().branchReceiptData.tokenPapers || 1),                        shiftStartHour: Number(offlineData.shift_start_hour || 15),
                        sendTokenToStaff: offlineData.send_token_to_staff !== '0',
                        offlineMode: true
                    }, offlineMode: true, isFirstFetch: false });
                    return get().branchReceiptData;
                }
            } catch (fallbackError) {
                console.error('Fallback offline branch data failed:', fallbackError);
            }
            throw error;
        }
    },

    setOfflineMode: (value) => {
        localStorage.setItem('branchOfflineMode', value ? 'true' : 'false');
        set({ offlineMode: value, branchReceiptData: { ...get().branchReceiptData, offlineMode: value } });
    },

    initializeOfflineState: async () => {
        try {
            const offlineFlag = await readOfflineFlag();
            if (offlineFlag) {
                const offlineSettings = await getOfflineBranchSettings();
                set({
                    offlineMode: true,
                    branchReceiptData: {
                        ...get().branchReceiptData,
                        offlineMode: true,
                        tokenNumber: Number(offlineSettings.token_number || get().branchReceiptData.tokenNumber || 1),
                        shiftStartHour: Number(offlineSettings.shift_start_hour || get().branchReceiptData.shiftStartHour || 15),
                        sendTokenToStaff: offlineSettings.send_token_to_staff !== '0'
                    }
                });
                localStorage.setItem('branchOfflineMode', 'true');
            } else if (localStorage.getItem('branchOfflineMode') === 'true') {
                set({
                    offlineMode: true,
                    branchReceiptData: { ...get().branchReceiptData, offlineMode: true }
                });
            }
            return offlineFlag;
        } catch (error) {
            console.warn('Unable to read offline mode state:', error);
            return false;
        }
    },

    initializeOfflineMode: async (branchId) => {
        const normalizedBranchId = String(branchId || '').replace(/^BR-/, '');
        if (!navigator.onLine) {
            throw new Error('Online connection required to enable offline mode.');
        }
        set({ offlineSyncing: true, offlineSyncError: null });

        try {
            await initializeOfflineDb(normalizedBranchId);
            await updateReceiptSettings({ branch_id: normalizedBranchId, offlineMode: true });
            localStorage.setItem('branchOfflineMode', 'true');
            set({ offlineMode: true, branchReceiptData: { ...get().branchReceiptData, offlineMode: true } });
            return true;
        } catch (error) {
            console.error('Error initializing offline mode:', error);
            set({ offlineSyncError: error.message || 'Failed to initialize offline mode.' });
            throw error;
        } finally {
            set({ offlineSyncing: false });
        }
    },

    disableOfflineMode: async (branchId) => {
        const normalizedBranchId = String(branchId || '').replace(/^BR-/, '');
        if (!navigator.onLine) {
            throw new Error('Online connection required to switch back to online mode.');
        }

        set({ offlineSyncing: true, offlineSyncError: null });
        try {
            const settings = await getOfflineBranchSettings();
            const currentTokenNumber = Number(settings.token_number || 1);
            await syncOfflineOrdersToServer(normalizedBranchId);
            await updateReceiptSettings({ branch_id: normalizedBranchId, offlineMode: false, tokenNumber: currentTokenNumber });
            await deleteDb();
            localStorage.setItem('branchOfflineMode', 'false');
            set({ offlineMode: false, branchReceiptData: { ...get().branchReceiptData, offlineMode: false } });
            return true;
        } catch (error) {
            console.error('Error disabling offline mode:', error);
            set({ offlineSyncError: error.message || 'Failed to synchronize offline data.' });
            throw error;
        } finally {
            set({ offlineSyncing: false });
        }
    },

    loadOfflineProducts: async () => {
        if (!get().offlineMode) {
            return [];
        }
        try {
            return await getOfflineProducts();
        } catch (error) {
            console.error('Error loading offline products:', error);
            return [];
        }
    },

    loadOfflineCategories: async () => {
        if (!get().offlineMode) {
            return [];
        }
        try {
            return await getOfflineCategories();
        } catch (error) {
            console.error('Error loading offline categories:', error);
            return [];
        }
    },

    // create an update function to send the updated branch receipt data to the backend
    updateBranchReceiptData: async (updatedData) => {
        try {
            const response = await updateReceiptSettings(updatedData);
            set({ branchReceiptData: updatedData });
            return response;
        } catch (error) {
            console.error('Error updating branch receipt data:', error);
            throw error;
        }
    },

    resetBranchTokenNumber: async (branchId) => {
        try {
            const response = await resetBranchTokenNumber(branchId);
            // Update the local state with the new token number
            if (response.success && response.branchSettings && typeof response.branchSettings.token_number !== 'undefined') {
                const newTokenNumber = Number(response.branchSettings.token_number);
                
                // Also update offline mode if it's currently active
                const offlineFlag = await readOfflineFlag();
                if (offlineFlag) {
                    await setOfflineBranchSetting('token_number', String(newTokenNumber));
                }
                
                set((state) => ({
                    branchReceiptData: {
                        ...state.branchReceiptData,
                        tokenNumber: newTokenNumber
                    }
                }));
            }
            return response;
        } catch (error) {
            console.error('Error resetting branch token number:', error);
            throw error;
        }
    },

});